import Logo from "../Logo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
const Footer = ({ mobileRedesign = false }: { mobileRedesign?: boolean } = {}) => {
  const desktop = (
    <footer className="relative py-16 px-4 bg-gradient-to-b from-gray-50 to-white border-t border-gray-100">
    {/* Background gradient accent */}
    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>

    <div className="container mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start gap-10 mb-12">
        <motion.div className="mb-8 md:mb-0" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }}>
          <div className="mb-4">
            <Logo variant="orange" />
          </div>
          <p className="text-gray-600 text-lg max-w-md leading-relaxed">Transform your wardrobe with expert stylists.</p>
          <div className="flex space-x-4 mt-6">
            <SocialLink href="https://instagram.com/hellocellocloset" icon="instagram" />
            <SocialLink href="https://www.linkedin.com/company/107869213" icon="linkedin" />
          </div>
        </motion.div>

        <motion.div className="grid grid-cols-2 gap-8 sm:gap-16 text-sm" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }}>
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Product</h4>
            <div className="space-y-3">
              <FooterLink href="/#features">Features</FooterLink>
              <FooterLink href="/howtoguide">How It Works</FooterLink>
              <FooterLink href="/#pricing">Pricing</FooterLink>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
            <div className="space-y-3">
              <FooterLink href="/about">About Us</FooterLink>
              <FooterLink href="/stylist">Our Stylists</FooterLink>
              <FooterLink href="/work-with-us">Work with us</FooterLink>
              <FooterLink href="/manifesto">Our Manifesto</FooterLink>
              <FooterLink href="/press">Press &amp; Media</FooterLink>
              <FooterLink href="/contact">Contact</FooterLink>
              <FooterLink href="/faq">FAQ</FooterLink>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }}>
        <p className="text-gray-500 text-sm mb-4 md:mb-0">
          © {new Date().getFullYear()} Cello. All rights reserved.
        </p>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
          <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link>
          <Link to="/sms" className="hover:text-primary transition-colors">SMS Policy</Link>
        </div>
      </motion.div>
    </div>
    </footer>
  );

  // Mobile footer — cream ground, orange logo, 2-column links, legal row.
  // Gated so only the splash opts in; desktop + other pages keep the footer above.
  const mobileFooter = (
    <footer className="md:hidden bg-[#FFF7F0] px-6 pt-14 pb-10">
      <div className="[&_img]:h-10 [&_div]:h-10">
        <Logo variant="orange" />
      </div>
      <p className="mt-3 text-[13px] leading-[1.6] text-[#57534e]">Transform your wardrobe with expert stylists.</p>
      <div className="mt-5 flex gap-3">
        <SocialLink href="https://instagram.com/hellocellocloset" icon="instagram" />
        <SocialLink href="https://www.linkedin.com/company/107869213" icon="linkedin" />
      </div>
      <div className="mt-8 grid max-w-[300px] grid-cols-2 gap-x-6 gap-y-7">
        <div className="flex flex-col items-start gap-2.5">
          <span className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#a39a89]">Product</span>
          <a href="/#features" className="text-[13px] text-[#2a241c]">Features</a>
          <Link to="/howtoguide" className="text-[13px] text-[#2a241c]">How It Works</Link>
          <a href="/#pricing" className="text-[13px] text-[#2a241c]">Pricing</a>
        </div>
        <div className="flex flex-col items-start gap-2.5">
          <span className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#a39a89]">Company</span>
          <Link to="/about" className="text-[13px] text-[#2a241c]">About Us</Link>
          <Link to="/stylist" className="text-[13px] text-[#2a241c]">Our Stylists</Link>
          <Link to="/work-with-us" className="text-[13px] text-[#2a241c]">Work with us</Link>
          <Link to="/manifesto" className="text-[13px] text-[#2a241c]">Our Manifesto</Link>
          <Link to="/press" className="text-[13px] text-[#2a241c]">Press &amp; Media</Link>
          <Link to="/contact" className="text-[13px] text-[#2a241c]">Contact</Link>
          <Link to="/faq" className="text-[13px] text-[#2a241c]">FAQ</Link>
        </div>
      </div>
      <div className="mt-10 flex flex-wrap gap-x-4 gap-y-2 border-t border-[#e7ded3] pt-5 text-[11px] text-[#78716c]">
        <span>© {new Date().getFullYear()} Cello. All rights reserved.</span>
        <Link to="/privacy-policy" className="hover:text-[#0f172a]">Privacy Policy</Link>
        <Link to="/terms" className="hover:text-[#0f172a]">Terms of Service</Link>
        <Link to="/sms" className="hover:text-[#0f172a]">SMS Policy</Link>
      </div>
    </footer>
  );

  if (!mobileRedesign) return desktop;

  return (
    <>
      {mobileFooter}
      <div className="hidden md:block">{desktop}</div>
    </>
  );
};
const MotionLink = motion(Link);
const FooterLink = ({
  href,
  children
}: {
  href: string;
  children: React.ReactNode;
}) => {
  // Internal page routes navigate client-side; hash anchors (/#...) and external
  // links stay as real anchors so they scroll / open correctly.
  const isRoute = href.startsWith("/") && !href.startsWith("/#");
  const className = "text-gray-600 hover:text-primary transition-colors duration-200 block w-fit";
  return isRoute ? (
    <MotionLink to={href} className={className} whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
      {children}
    </MotionLink>
  ) : (
    <motion.a href={href} className={className} whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
      {children}
    </motion.a>
  );
};
const SocialLink = ({
  href,
  icon
}: {
  href: string;
  icon: string;
}) => <motion.a href={href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-100 hover:bg-primary hover:text-white rounded-full flex items-center justify-center transition-all duration-200" whileHover={{
  scale: 1.1,
  y: -2
}} transition={{
  type: "spring",
  stiffness: 400,
  damping: 10
}}>
    {icon === "instagram" && <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.045-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
    </svg>}
    {icon === "linkedin" && <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path fillRule="evenodd" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" clipRule="evenodd" />
    </svg>}
  </motion.a>;
export default Footer;